import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { foyerApi, type ContentPackageRow, type SeatUnit } from "../http/foyer";
import { describeError, useToast } from "./toast";
import { useTranslation } from "react-i18next";
import { friendlyError } from "../utils/apiError";
import { tokens } from "../theme";
import { EditDrawer, type Dismiss } from "./EditDrawer";

/**
 * Choose the packages a product grants.
 *
 * ## Why this did not exist
 *
 * `setFeatureSetPackages` has been in the API client for a while and **nothing
 * ever called it**. Products arrived from the portal already carrying their
 * grants, so the screen only ever needed to DISPLAY them — and once the import
 * was cancelled, a product could be created and then never given anything. The
 * chain was: package authorable, product creatable, and no way to connect the
 * two.
 *
 * Found the same way the last three gaps were: by walking the flow rather than
 * reading the routes.
 *
 * ## What it shows, and why not just names
 *
 * A package's NAME does not tell an operator whether it is the right one —
 * `svt-channels` and `svt-channels-hd` look identical in a list. So each row
 * carries what the package actually holds: how many titles are pinned, and
 * whether a rule is carrying more on top. That is the difference between
 * picking confidently and picking twice.
 *
 * ## The save REPLACES, and the route says so
 *
 * `PUT …/feature-sets/:id` refuses a partial list precisely because a partial
 * one would silently revoke the rest. This dialog therefore holds the complete
 * set at all times: it opens from what the product already grants, and every
 * change since has been a tick.
 *
 * ⚠️ A grant pointing at a package Foyer does not hold is refused by the route
 * rather than stored. Missing ids are shown here so an operator can see what
 * they are about to lose — but they cannot be re-saved, which is the correct
 * asymmetry: a dangling grant grants nothing, silently, and the cheapest moment
 * to catch it is the one where somebody is already looking at it.
 */
export default function ProductPackagePicker({
  productId,
  productName,
  granted,
  seatUnit: initialSeatUnit,
  onClose,
  onSaved,
}: {
  productId: string;
  productName: string;
  granted: { id: string; name: string; missing: boolean }[];
  /** What a licence for this product counts. `null` = it has no amount. */
  seatUnit: SeatUnit;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const toast = useToast();
  const { t } = useTranslation(["content", "common"]);
  /**
   * Editable HERE because this is where somebody is already looking at the
   * product, and because the products authored before the field existed all
   * have `null` — which hides the Amount field on the licence dialog. Without
   * somewhere to correct it, those products can be sold and never quantified.
   */
  const [seatUnit, setSeatUnit] = useState<SeatUnit>(initialSeatUnit);
  const [packages, setPackages] = useState<ContentPackageRow[] | null>(null);
  const [picked, setPicked] = useState<Set<string>>(
    new Set(granted.filter((g) => !g.missing).map((g) => g.id))
  );
  const [filter, setFilter] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await foyerApi.listContentPackages();
      setPackages(res.packages);
    } catch (e) {
      setError(friendlyError(t, e));
      setPackages([]);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Granted ids Foyer no longer holds. Shown, and deliberately not saveable. */
  const dangling = granted.filter((g) => g.missing);

  const visible = useMemo(() => {
    if (!packages) return [];
    const q = filter.trim().toLowerCase();
    if (!q) return packages;
    return packages.filter(
      (p) =>
        (p.name ?? "").toLowerCase().includes(q) ||
        p.id.toLowerCase().includes(q)
    );
  }, [packages, filter]);

  const toggle = (id: string) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const save = async (dismiss: Dismiss) => {
    setBusy(true);
    setError(null);
    try {
      await foyerApi.setFeatureSetPackages(productId, [...picked], seatUnit);
      toast.success(
        picked.size === 0
          ? t("productPackagePicker.savedNone", { name: productName })
          : t("productPackagePicker.saved", {
              name: productName,
              count: picked.size,
            })
      );
      // Slide away first; the page then reloads and unmounts the drawer.
      dismiss(() => void onSaved());
    } catch (e) {
      setError(friendlyError(t, e));
      toast.error(
        describeError(t, "saveProductPackages", e, { product: productName })
      );
      setBusy(false);
    }
  };

  return (
    <EditDrawer
      open
      onClose={onClose}
      busy={busy}
      title={t("productPackagePicker.title", { name: productName })}
      subtitle={t("productPackagePicker.subtitle")}
      data-testid="product-grants-drawer"
      actions={(dismiss) => (
        <>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mr: "auto", alignSelf: "center" }}
          >
            {t("productPackagePicker.pickedCount", { count: picked.size })}
          </Typography>
          <Button onClick={() => dismiss()} disabled={busy}>
            {t("common:buttons.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={() => void save(dismiss)}
            disabled={busy}
            data-testid="product-grants-save"
          >
            {t("common:buttons.save")}
          </Button>
        </>
      )}
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        {error ? <Alert severity="error">{error}</Alert> : null}

        <TextField
          label={t("productPackagePicker.seatUnitLabel")}
          select
          size="small"
          value={seatUnit ?? "none"}
          onChange={(e) =>
            setSeatUnit(
              e.target.value === "none" ? null : (e.target.value as SeatUnit)
            )
          }
          inputProps={{ "data-testid": "product-seat-unit" }}
          helperText={
            seatUnit === null && picked.size > 0
              ? t("productPackagePicker.seatUnitCountsNothing")
              : t("productPackagePicker.seatUnitHelp")
          }
          error={seatUnit === null && picked.size > 0}
        >
          {/* Each option is the sentence an operator would say out loud.
                "No amount" read as "free"; "Nothing to count" says what it is. */}
          <MenuItem value="room">
            {t("productPackagePicker.seatUnit.room")}
          </MenuItem>
          <MenuItem value="screen">
            {t("productPackagePicker.seatUnit.screen")}
          </MenuItem>
          <MenuItem value="resident">
            {t("productPackagePicker.seatUnit.resident")}
          </MenuItem>
          <MenuItem value="none">
            {t("productPackagePicker.seatUnit.none")}
          </MenuItem>
        </TextField>

        {dangling.length > 0 ? (
          <Alert severity="warning" data-testid="product-dangling">
            {t("productPackagePicker.dangling", {
              count: dangling.length,
              ids: dangling.map((d) => d.id).join(", "),
            })}
          </Alert>
        ) : null}

        <TextField
          size="small"
          label={t("common:labels.filter")}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t("productPackagePicker.filterPlaceholder")}
        />

        {packages === null ? (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={26} />
          </Stack>
        ) : visible.length === 0 ? (
          <Alert severity="info">
            {filter.trim()
              ? t("productPackagePicker.noMatch")
              : t("productPackagePicker.noPackages")}
          </Alert>
        ) : (
          // No inner scroll box: the drawer body scrolls.
          <Box>
            {visible.map((p) => (
              <Stack
                key={p.id}
                direction="row"
                alignItems="center"
                spacing={1}
                onClick={() => toggle(p.id)}
                data-testid={`grant-${p.id}`}
                sx={{
                  py: 0.75,
                  px: 0.5,
                  cursor: "pointer",
                  borderBottom: 1,
                  borderColor: "divider",
                  "&:hover": { background: tokens.rowAlt },
                }}
              >
                <Checkbox
                  size="small"
                  checked={picked.has(p.id)}
                  tabIndex={-1}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => toggle(p.id)}
                />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" noWrap>
                    {p.name || p.id}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontFamily: "fontFamilyMono",
                      color: tokens.inkMuted,
                    }}
                    noWrap
                  >
                    {p.id}
                  </Typography>
                </Box>
                {/* A name does not say whether this is the right package.
                      What it HOLDS does. */}
                <Stack direction="row" spacing={0.5}>
                  {p.assets.length > 0 ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={t("productPackagePicker.picked", {
                        count: p.assets.length,
                      })}
                    />
                  ) : null}
                  {p.rule ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      color="primary"
                      label={
                        p.resolved?.inert
                          ? t("productPackagePicker.ruleInert")
                          : t("productPackagePicker.ruleCount", {
                              count: `${p.resolved?.ruleCount ?? 0}${
                                p.resolved?.truncated ? "+" : ""
                              }`,
                            })
                      }
                    />
                  ) : null}
                  {p.assets.length === 0 && !p.rule ? (
                    <Chip
                      size="small"
                      color="warning"
                      label={t("productPackagePicker.empty")}
                    />
                  ) : null}
                </Stack>
              </Stack>
            ))}
          </Box>
        )}
      </Stack>
    </EditDrawer>
  );
}
